import { QueryClient } from "@tanstack/react-query";

/**
 * Build a fresh {@link QueryClient}. The server MUST use one per request — a
 * shared singleton would leak one visitor's cache into another's response; the
 * browser reuses a single client ({@link getQueryClient}).
 *
 * A non-zero `staleTime` keeps SSR-seeded data fresh so it doesn't refetch on
 * mount, and focus/reconnect refetch is off — an unprompted refetch would also
 * swap draft content for published, since the client query isn't draft-aware.
 */
export function makeQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60_000,
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,
			},
		},
	});
}

let browserQueryClient: QueryClient | undefined;

/**
 * The browser's {@link QueryClient} singleton, reused for the page's lifetime so
 * the server-hydrated cache survives re-renders. It's fresh per document load
 * (this is an MPA). Server code MUST use {@link makeQueryClient} per request.
 */
export function getQueryClient(): QueryClient {
	return (browserQueryClient ??= makeQueryClient());
}
